import { clearMeasurementStorage, startMeasurement } from './measurement.js';

const choiceKey = 'kubis-privacy-choice';
const pendingKey = choiceKey + '-switch';
const validFor = 180 * 24 * 60 * 60 * 1000;
const none = { analytics: false, ads: false };

function readChoice(revision, destinations) {
  try {
    const record = JSON.parse(localStorage.getItem(choiceKey));
    if (!record) return null;
    if (record.revision !== revision || record.destinations !== destinations ||
        !Number.isFinite(record.decidedAt) || record.decidedAt > Date.now() + 60000 || Date.now() - record.decidedAt >= validFor) {
      localStorage.removeItem(choiceKey);
      return null;
    }
    if (typeof record.analytics !== 'boolean' || typeof record.ads !== 'boolean') return null;
    if ((record.analytics || record.ads) && !/^[a-f0-9]{32}$/.test(record.receipt)) return null;
    return record;
  } catch { return null; }
}

function writeChoice(record) {
  try { localStorage.setItem(choiceKey, JSON.stringify(record)); return true; }
  catch { return false; }
}

async function confirmGrant(revision, ids, desired, signal) {
  const response = await fetch('api/consent.php', {
    method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'origin', signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      revision,
      purposes: { analytics: desired.analytics, advertising: desired.ads },
      destinations: [desired.analytics && ids.analytics, desired.ads && ids.ads].filter(Boolean),
    }),
  });
  if (response.status !== 201) throw new Error('Consent receipt unavailable');
  const { receipt, recordedAt } = await response.json();
  if (!/^[a-f0-9]{32}$/.test(receipt) || !Number.isFinite(Date.parse(recordedAt))) throw new Error('Invalid receipt');
  return { receipt, recordedAt };
}

export function initializeConsent(config, publicHash = () => '') {
  const { revision } = config;
  const ids = {
    analytics: /^G-[A-Z0-9]+$/.test(config.analyticsId) ? config.analyticsId : '',
    ads: /^AW-[0-9]+$/.test(config.adsId) ? config.adsId : '',
  };
  if (!ids.analytics && !ids.ads) {
    try {
      if (localStorage.getItem(choiceKey)) {
        clearMeasurementStorage(false, false);
        localStorage.removeItem(choiceKey);
      }
    } catch { /* No optional service is active. */ }
    return;
  }
  const destinations = [ids.analytics, ids.ads].filter(Boolean).join(',');
  const panel = document.getElementById('privacy-choice');
  const analytics = panel.querySelector('[name="analytics"]');
  const ads = panel.querySelector('[name="ads"]');
  analytics.closest('label').hidden = !ids.analytics;
  ads.closest('label').hidden = !ids.ads;
  const purposeNames = [];
  if (ids.analytics) purposeNames.push('meranie návštevnosti cez Google Analytics');
  if (ids.ads) purposeNames.push('meranie kampaní cez Google Ads');
  for (const status of document.querySelectorAll('[data-measurement-status]')) {
    status.textContent = 'Tento web ponúka ' + purposeNames.join(' a ') + '. Každý účel sa aktivuje iba po vašom súhlase.';
  }
  let choice = readChoice(revision, destinations);
  let runtime;
  let request;
  let expiryTimer;
  let trigger;
  const message = panel.querySelector('[data-consent-message]');
  function syncFields() {
    analytics.checked = Boolean(choice?.analytics);
    ads.checked = Boolean(choice?.ads);
  }
  function show() {
    syncFields();
    panel.hidden = false;
  }
  function close() {
    panel.hidden = true;
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    trigger = null;
  }
  function onPrivacyPage() { return Boolean(document.querySelector('#view-privacy.active-view')); }
  function refresh() {
    const saved = readChoice(revision, destinations);
    // An executing third-party library cannot be unloaded. Start a clean document if its scope changes.
    if (runtime && (choice?.ads !== saved?.ads || choice?.analytics !== saved?.analytics)) {
      runtime.stop();
      clearMeasurementStorage(saved?.analytics, saved?.ads);
      location.reload();
      return;
    }
    choice = saved;
    clearMeasurementStorage(choice?.analytics, choice?.ads);
    if (!runtime && choice) runtime = startMeasurement(ids, choice, publicHash);
    clearTimeout(expiryTimer);
    if (choice) {
      expiryTimer = setTimeout(refresh, Math.min(choice.decidedAt + validFor - Date.now(), 2147483647));
      close();
    } else if (!onPrivacyPage()) {
      show();
    } else {
      close();
    }
  }
  function store(desired, proof, decidedAt = Date.now()) {
    return writeChoice({ revision, destinations, decidedAt, updatedAt: Date.now(), ...desired, ...proof });
  }
  async function apply(desired) {
    request?.abort();
    request = null;
    message.textContent = '';
    const previous = choice || none;
    const adds = (desired.analytics && !previous.analytics) || (desired.ads && !previous.ads);
    const removes = (previous.analytics && !desired.analytics) || (previous.ads && !desired.ads);
    if (removes) {
      const kept = { analytics: previous.analytics && desired.analytics, ads: previous.ads && desired.ads };
      // The existing receipt covers the subset. Withdrawal never depends on a network response.
      const someKept = kept.analytics || kept.ads;
      const proof = someKept ? { receipt: previous.receipt, recordedAt: previous.recordedAt } : {};
      const narrowed = store(kept, proof, someKept ? previous.decidedAt : Date.now());
      if (!narrowed) {
        try { localStorage.removeItem(choiceKey); } catch { /* Browser storage is unavailable. */ }
      }
      if (adds && narrowed) {
        // For a switch between purposes, remove the old script first, then request the newly chosen scope.
        try { sessionStorage.setItem(pendingKey, JSON.stringify({ desired, previous: localStorage.getItem(choiceKey), when: Date.now() })); } catch { /* Fail closed. */ }
      }
      runtime?.stop();
      clearMeasurementStorage(kept.analytics, kept.ads);
      location.reload();
      return;
    }
    if (!adds) {
      if (!choice) {
        if (store(none, {})) { refresh(); return; }
        choice = { ...none, decidedAt: Date.now() };
      }
      close();
      return;
    }
    const controller = new AbortController();
    request = controller;
    const earlier = readChoice(revision, destinations);
    message.textContent = 'Ukladáme vašu voľbu…';
    const timeout = setTimeout(() => controller.abort(), 7000);
    try {
      const proof = await confirmGrant(revision, ids, desired, controller.signal);
      if (request !== controller) return;
      if (readChoice(revision, destinations)?.updatedAt !== earlier?.updatedAt) { refresh(); return; }
      if (!store(desired, proof)) throw new Error('Cannot remember the receipt');
      refresh();
    } catch {
      if (request !== controller) return;
      message.textContent = 'Nový súhlas sa nepodarilo uložiť. Vybrané meranie sa nezaplo. Skúste to neskôr alebo ho odmietnite.';
      panel.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (request === controller) request = null;
    }
  }
  for (const button of document.querySelectorAll('[data-consent-settings]')) {
    button.hidden = false;
    button.addEventListener('click', () => {
      trigger = button;
      show();
      panel.querySelector('h2').focus({ preventScroll: true });
    });
  }
  panel.addEventListener('click', event => {
    if (event.target.closest('[data-page="privacy"]')) { close(); return; }
    const button = event.target.closest('button');
    if (!button) return;
    const all = button.value === 'all';
    void apply({
      analytics: Boolean(ids.analytics && (all || (button.value === 'selection' && analytics.checked))),
      ads: Boolean(ids.ads && (all || (button.value === 'selection' && ads.checked))),
    });
  });
  panel.addEventListener('keydown', event => {
    if (event.key === 'Escape') close();
  });
  document.addEventListener('kubis:pageview', () => {
    if (onPrivacyPage()) close();
    else if (!choice) show();
    runtime?.pageview();
  });
  window.addEventListener('storage', event => {
    if (event.key !== choiceKey && event.key !== null) return;
    request?.abort();
    request = null;
    refresh();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && choice?.updatedAt !== readChoice(revision, destinations)?.updatedAt) refresh();
  });
  refresh();
  try {
    const pending = JSON.parse(sessionStorage.getItem(pendingKey));
    sessionStorage.removeItem(pendingKey);
    if (pending && pending.previous === localStorage.getItem(choiceKey) && Date.now() - pending.when < 30000) {
      void apply({ analytics: Boolean(ids.analytics && pending.desired.analytics), ads: Boolean(ids.ads && pending.desired.ads) });
    }
  } catch { /* No unconfirmed grant can activate a tag. */ }
}
